import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useStartDate } from '../StartDateContext';

export const useUndoRedo = (panelGroupsContainerRef, maxHistorySize = 20, onDataChange = null) => {
  const { startDate, setStartDate } = useStartDate();
  const [undoHistory, setUndoHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const currentHistoryIndexRef = useRef(-1);
  const isUndoRedoInProgressRef = useRef(false);
  const lastRestoreTimestampRef = useRef(0);
  const isLoadingInProgressRef = useRef(false); // Neues Flag für Lade-Operationen

  // Synchronisiere Ref mit State
  useEffect(() => {
    currentHistoryIndexRef.current = currentHistoryIndex;
  }, [currentHistoryIndex]);

  const saveToHistory = useCallback(() => {
    // Verhindere Saves kurz nach Restore-Operationen
    const timeSinceLastRestore = Date.now() - lastRestoreTimestampRef.current;
    if (timeSinceLastRestore < 2500) {
      return;
    }

    if (isUndoRedoInProgressRef.current) {
      return;
    }

    // Verhindere Saves während Lade-Operationen
    if (isLoadingInProgressRef.current) {
      return;
    }

    let panelGroupsData = [];
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      panelGroupsData = containerData.groups || [];
    } else {
      return;
    }

    // Verhindere das Speichern von komplett leeren Zuständen (außer beim ersten Mal)
    if (panelGroupsData.length === 0 && undoHistory.length > 0) {
      return;
    }

    const historyEntry = {
      timestamp: new Date().toISOString(),
      startDate: startDate.toISOString(),
      panelGroups: JSON.parse(JSON.stringify(panelGroupsData))
    };

    setUndoHistory(prevHistory => {
      // Duplikate vermeiden
      if (prevHistory.length > 0) {
        const lastEntry = prevHistory[currentHistoryIndexRef.current];
        if (lastEntry && JSON.stringify(lastEntry.panelGroups) === JSON.stringify(historyEntry.panelGroups)) {
          return prevHistory;
        }
      }
      // Branching: schneide alles nach aktuellem Index ab
      let newHistory = prevHistory.slice(0, currentHistoryIndexRef.current + 1);
      newHistory.push(historyEntry);
      while (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }
      
      // Setze Index nur auf das neue Ende, wenn wirklich ein neuer Eintrag hinzugefügt wurde
      if (newHistory.length > prevHistory.length) {
        const newIndex = newHistory.length - 1;
        setTimeout(() => {
          setCurrentHistoryIndex(newIndex);
          currentHistoryIndexRef.current = newIndex;
        }, 0);
      }
      
      return newHistory;
    });
  }, [panelGroupsContainerRef, startDate, maxHistorySize, undoHistory.length]);

  // Setze Index nur bei Initialisierung
  useEffect(() => {
    if (undoHistory.length > 0 && currentHistoryIndex === -1) {
      const newIndex = undoHistory.length - 1;
      setCurrentHistoryIndex(newIndex);
      currentHistoryIndexRef.current = newIndex;
    }
  }, [undoHistory.length, currentHistoryIndex]);

  // Einfache Funktion zum direkten Setzen der Daten ohne Effects
  const restoreState = useCallback((state) => {
    if (!state || !panelGroupsContainerRef.current) {
      return;
    }

    isUndoRedoInProgressRef.current = true;
    lastRestoreTimestampRef.current = Date.now();
    
    try {
      // Setze StartDate
      setStartDate(new Date(state.startDate));
      
      // Setze die Panel-Daten direkt
      panelGroupsContainerRef.current.forceSetData({
        groups: state.panelGroups
      });
      
    } catch (error) {
      console.warn('Fehler beim Wiederherstellen des Zustands:', error);
    } finally {
      // Setze das Flag zurück
      setTimeout(() => {
        isUndoRedoInProgressRef.current = false;
      }, 2000);
    }
  }, [panelGroupsContainerRef, setStartDate]);

  const handleUndo = useCallback(() => {
    const currentIndex = currentHistoryIndexRef.current;
    
    if (currentIndex <= 0) {
      return;
    }

    const targetIndex = currentIndex - 1;
    const previousState = undoHistory[targetIndex];
    
    if (previousState) {
      setCurrentHistoryIndex(targetIndex);
      currentHistoryIndexRef.current = targetIndex;
      restoreState(previousState);
    }
  }, [undoHistory, restoreState]);

  const handleRedo = useCallback(() => {
    const currentIndex = currentHistoryIndexRef.current;
    
    if (currentIndex >= undoHistory.length - 1) {
      return;
    }

    const targetIndex = currentIndex + 1;
    const nextState = undoHistory[targetIndex];
    
    if (nextState) {
      setCurrentHistoryIndex(targetIndex);
      currentHistoryIndexRef.current = targetIndex;
      restoreState(nextState);
    }
  }, [undoHistory, restoreState]);

  const canUndo = useMemo(() => {
    return currentHistoryIndex > 0;
  }, [currentHistoryIndex]);
  
  const canRedo = useMemo(() => {
    return currentHistoryIndex < undoHistory.length - 1;
  }, [currentHistoryIndex, undoHistory.length]);

  const handleDataChange = useCallback(() => {
    if (isUndoRedoInProgressRef.current) {
      return;
    }
    
    // Verhindere Undo-Einträge während Lade-Operationen
    if (isLoadingInProgressRef.current) {
      return;
    }
    
    if (panelGroupsContainerRef.current) {
      saveToHistory();
      
      // Rufe den Callback auf, wenn bereitgestellt
      if (onDataChange) {
        // Kleiner Timeout, damit die Änderungen erst verarbeitet werden
        setTimeout(onDataChange, 100);
      }
    }
  }, [saveToHistory, onDataChange, panelGroupsContainerRef]);

  // Initialer Snapshot beim Laden der Komponente
  useEffect(() => {
    const createInitialSnapshot = () => {
      if (panelGroupsContainerRef.current) {
        saveToHistory();
      }
    };
    
    const timer = setTimeout(createInitialSnapshot, 1000);
    return () => clearTimeout(timer);
  }, [panelGroupsContainerRef, saveToHistory]);

  // Tastenkürzel für Undo/Redo
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) handleUndo();
      } else if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'Z')) {
        e.preventDefault();
        if (canRedo) handleRedo();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [canUndo, canRedo, handleUndo, handleRedo]);

  // Funktionen für Lade-Operationen
  const disableUndoDuringLoad = useCallback(() => {
    isLoadingInProgressRef.current = true;
  }, []);

  const enableUndoAfterLoad = useCallback(() => {
    isLoadingInProgressRef.current = false;
  }, []);

  const clearHistory = useCallback(() => {
    setUndoHistory([]);
    setCurrentHistoryIndex(-1);
    currentHistoryIndexRef.current = -1;
  }, []);

  const resetHistoryAfterLoad = useCallback(() => {
    // Lösche die gesamte Historie und erstelle einen neuen initialen Zustand
    clearHistory();
    
    // Erstelle einen neuen Snapshot nach dem Laden
    setTimeout(() => {
      if (panelGroupsContainerRef.current) {
        saveToHistory();
      }
    }, 500); // Kurze Verzögerung, damit das Laden abgeschlossen ist
  }, [clearHistory, saveToHistory, panelGroupsContainerRef]);

  return {
    undoHistory,
    currentHistoryIndex,
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    handleDataChange,
    saveToHistory,
    // Neue Funktionen für Lade-Operationen
    disableUndoDuringLoad,
    enableUndoAfterLoad,
    clearHistory,
    resetHistoryAfterLoad
  };
};
